/**
 * Onglet État de santé du système
 * @module admin/tabs/HealthTab
 */

import React, { useEffect } from 'react';
import { useHealth } from '../hooks/useHealth';

const HealthTab = () => {
  const {
    healthStatus,
    startupHealth,
    healthLoading,
    healthError,
    fetchStartupHealth,
    fetchCurrentHealth,
    handleRefreshHealth,
  } = useHealth();

  useEffect(() => {
    fetchStartupHealth();
    fetchCurrentHealth();
  }, [fetchStartupHealth, fetchCurrentHealth]);

  const renderHealthStatus = (status, label) => {
    if (!status) return null;

    const statusConfig = {
      healthy: { icon: '✅', text: 'Système sain', class: 'admin-health-healthy' },
      degraded: { icon: '⚠️', text: 'Système dégradé', class: 'admin-health-degraded' },
      unhealthy: { icon: '❌', text: 'Système non sain', class: 'admin-health-unhealthy' },
    };

    const config = statusConfig[status.overall] || statusConfig.unhealthy;

    return (
      <div className="admin-health-section">
        <h3>{label}</h3>
        <div className={`admin-health-status admin-health-${status.overall}`}>
          <div className="admin-health-status-header">
            <span className="admin-health-status-icon">{config.icon}</span>
            <span className="admin-health-status-text">{config.text}</span>
          </div>
          <div className="admin-health-timestamp">
            {label.includes('démarrage')
              ? `Démarrage: ${new Date(status.timestamp).toLocaleString('fr-FR')}`
              : `Dernière vérification: ${new Date(status.timestamp).toLocaleString('fr-FR')}`}
          </div>
        </div>

        <div className="admin-health-details">
          <h4>Vérifications {label.includes('démarrage') ? 'au démarrage' : 'complètes'}:</h4>
          {status.checks && (
            <div className="admin-health-checks">
              {status.checks.database && (
                <div className="admin-health-check">
                  <span
                    className={`admin-health-check-icon ${
                      status.checks.database.status === 'ok' ? 'success' : 'error'
                    }`}
                  >
                    {status.checks.database.status === 'ok' ? '✅' : '❌'}
                  </span>
                  <span className="admin-health-check-label">Base de données:</span>
                  <span className="admin-health-check-message">
                    {status.checks.database.message}
                  </span>
                </div>
              )}

              {status.checks.encryption && (
                <div className="admin-health-check">
                  <span
                    className={`admin-health-check-icon ${
                      status.checks.encryption.status === 'ok' ? 'success' : 'error'
                    }`}
                  >
                    {status.checks.encryption.status === 'ok' ? '✅' : '❌'}
                  </span>
                  <span className="admin-health-check-label">Clé de chiffrement:</span>
                  <span className="admin-health-check-message">
                    {status.checks.encryption.message}
                  </span>
                </div>
              )}

              {status.checks.mail && (
                <div className="admin-health-check">
                  <span
                    className={`admin-health-check-icon ${
                      status.checks.mail.status === 'ok'
                        ? 'success'
                        : status.checks.mail.status === 'warning'
                        ? 'warning'
                        : 'error'
                    }`}
                  >
                    {status.checks.mail.status === 'ok'
                      ? '✅'
                      : status.checks.mail.status === 'warning'
                      ? '⚠️'
                      : '❌'}
                  </span>
                  <span className="admin-health-check-label">Serveur mail:</span>
                  <span className="admin-health-check-message">
                    {status.checks.mail.message}
                  </span>
                </div>
              )}

              {status.checks.twitch && (
                <div className="admin-health-check-group">
                  <h5>Twitch:</h5>
                  {Object.entries(status.checks.twitch).map(([key, check]) => (
                    <div key={key} className="admin-health-check">
                      <span
                        className={`admin-health-check-icon ${
                          check.status === 'ok'
                            ? 'success'
                            : check.status === 'warning'
                            ? 'warning'
                            : 'error'
                        }`}
                      >
                        {check.status === 'ok'
                          ? '✅'
                          : check.status === 'warning'
                          ? '⚠️'
                          : '❌'}
                      </span>
                      <span className="admin-health-check-label">{key}:</span>
                      <span className="admin-health-check-message">{check.message}</span>
                    </div>
                  ))}
                </div>
              )}

              {status.checks.spotify && (
                <div className="admin-health-check-group">
                  <h5>Spotify:</h5>
                  {Object.entries(status.checks.spotify).map(([key, check]) => (
                    <div key={key} className="admin-health-check">
                      <span
                        className={`admin-health-check-icon ${
                          check.status === 'ok'
                            ? 'success'
                            : check.status === 'warning'
                            ? 'warning'
                            : 'error'
                        }`}
                      >
                        {check.status === 'ok'
                          ? '✅'
                          : check.status === 'warning'
                          ? '⚠️'
                          : '❌'}
                      </span>
                      <span className="admin-health-check-label">{key}:</span>
                      <span className="admin-health-check-message">{check.message}</span>
                    </div>
                  ))}
                </div>
              )}

              {status.checks.services && (
                <div className="admin-health-check-group">
                  <h5>Services:</h5>
                  {Object.entries(status.checks.services).map(([serviceName, serviceCheck]) => (
                    <div key={serviceName} className="admin-health-check">
                      <span
                        className={`admin-health-check-icon ${
                          serviceCheck.status === 'ok'
                            ? 'success'
                            : serviceCheck.status === 'warning'
                            ? 'warning'
                            : 'error'
                        }`}
                      >
                        {serviceCheck.status === 'ok'
                          ? '✅'
                          : serviceCheck.status === 'warning'
                          ? '⚠️'
                          : '❌'}
                      </span>
                      <span className="admin-health-check-label">{serviceName}:</span>
                      <span className="admin-health-check-message">
                        {serviceCheck.message}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="admin-section">
      <div className="admin-section-header">
        <h2>🏥 État de santé du système</h2>
        <button
          onClick={handleRefreshHealth}
          disabled={healthLoading}
          className="admin-button admin-button-primary"
        >
          {healthLoading ? '🔄 Rafraîchissement...' : '🔄 Rafraîchir'}
        </button>
      </div>

      {healthError && (
        <div className="admin-result admin-result-error">
          <h3>Erreur</h3>
          <p>{healthError}</p>
        </div>
      )}

      {renderHealthStatus(startupHealth, 'Rapport de démarrage')}
      {renderHealthStatus(healthStatus, 'État en temps réel')}

      {!startupHealth && !healthStatus && !healthLoading && (
        <div className="admin-placeholder">
          <p>
            Cliquez sur "Rafraîchir" pour effectuer une vérification de santé.
          </p>
        </div>
      )}
    </section>
  );
};

export default HealthTab;
